import * as t from 'io-ts';

import { dbModelId, ID } from '@main/schema-utils';

/** data subject region detection */
export enum CollectDataSubjectRegions {
  DoNotCollect = 'DO_NOT_COLLECT',
  AutoDetect = 'AUTO_DETECT',
  Collect = 'COLLECT',
}

/**
 * workflows statuses
 */
export enum WorkflowConfigStatus {
  /** live */
  Live = 'ENABLED',
  /** offline */
  Offline = 'DISABLED',
}

/**
 * Workflow instance statuses
 */
export enum WorkflowInstanceStatus {
  /** Workflow has been created and is waiting execution */
  Initialized = 'INITIALIZED',
  /** If the workflow is running normally */
  InProgress = 'IN_PROGRESS',
  /** if the workflow has been processed and is ready to be picked back up for the next phase */
  WaitingForNextPhase = 'WAITING_FOR_NEXT_PHASE',
  /** If the workflow has been canceled */
  Canceled = 'CANCELED',
  /** Awaiting human interaction, approval, manual phase transition, etc */
  AwaitingInteraction = 'AWAITING_INTERACTION',
  /** Execution has been paused */
  Paused = 'PAUSED',
  /** If the workflow has thrown any sort of error while processing but is not stopped */
  InProgressWithErrors = 'IN_PROGRESS_WITH_ERRORS',
  /** Completed with no outstanding errors */
  CompletedSuccessfully = 'COMPLETED_SUCCESSFULLY',
  /** Managed to complete, but encountered errors in doing so */
  CompletedWIthErrors = 'COMPLETED_WITH_ERRORS',
}

/**
 * Workflow job statuses
 */
export enum WorkflowJobStatus {
  /** Job has been created and is waiting for dependencies */
  Waiting = 'WAITING',
  /** Job has been queued by scheduler and is waiting to be picked up by processor */
  Queued = 'QUEUED',
  /** Job has completed work successfully */
  Success = 'SUCCESS',
  /** Job has failed with error */
  Error = 'ERROR',
  /** Job has been sent to a secondary processor queue like SQS for integrations */
  SentToSecondaryQueue = 'SENT_TO_SECONDARY_QUEUE',
  /** Job is waiting for response from external service */
  AwaitingExternalResponse = 'AWAITING_EXTERNAL_RESPONSE',
  /** Awaiting human interaction such as verification via sent email or text, etc */
  AwaitingInteraction = 'AWAITING_INTERACTION',
}

/** The completion state of a workflow step */
export enum WorkflowStepCompletionState {
  Completed = 'COMPLETED',
  NotCompleted = 'NOT_COMPLETED',
}

/**
 * All phases that a workflow can be in, which determine which jobs are allowed to be scheduled
 */
export enum WorkflowPhase {
  /** before the workflow has been picked up by the scheduler daemon */
  Initialization = 'INITIALIZATION',
  /** verifying identifiers in pre-flight checks */
  Verification = 'VERIFICATION',
  /** enriching identifiers and files */
  Enrichment = 'ENRICHMENT',
  /** acting on collected data from specific integration datapoints */
  DataPointAction = 'DATAPOINT_ACTION',
  /** No more jobs to be queued */
  JobsCompleted = 'JOBS_COMPLETED',
}

/**
 * Workflow Phases that have jobs associated with them. I wish there was a better way to just have a subset
 * of an existing enum, but I am not aware of one
 */
export enum WorkflowPhasesWithJobs {
  /** verifying identifiers in pre-flight checks */
  Verification = WorkflowPhase.Verification,
  /** enriching identifiers and files */
  Enrichment = WorkflowPhase.Enrichment,
  /** acting on collected data from specific integration datapoints */
  DataPointAction = WorkflowPhase.DataPointAction,
}

/**
 * Source models that are associated with jobs
 */
export enum WorkflowJobSourceModelName {
  Enricher = 'enricher',
  DataPoint = 'dataPoint',
  DataSilo = 'dataSilo',
  ProfileDataPoint = 'ProfileDataPoint',
}
/**
 * Destination models that are associated with jobs
 */
export enum WorkflowJobDestinationModelName {
  RequestEnricher = 'RequestEnricher',
  ProfileDataPoint = 'ProfileDataPoint',
  RequestDataSilo = 'RequestDataSilo',
}
/**
 * db model id types for destination models
 */
export type WorkflowJobDestinationId =
  | ID<'RequestEnricher'>
  | ID<'RequestDataSilo'>
  | ID<'ProfileDataPoint'>;

/**
 * types of things that can trigger a workflow
 */
export enum WorkflowTrigger {
  /** for workflows that have been initiated because of a DSR */
  Request = 'REQUEST',
}

/** The action to take when an id is missing from a verification enricher */
export enum MissingIdBehavior {
  Skip = 'SKIP',
  Cancel = 'CANCEL',
  Notify = 'NOTIFY',
}

/** The types of communication a WorkflowConfigCommunication row will handle */
export enum WorkflowCommunicationMethod {
  Email = 'EMAIL',
}

/** Specific type of template represented by the WorkflowConfigCommunication row */
export enum WorkflowCommunicationTemplateType {
  /** action - receiptTemplateId */
  ActionsReceipt = 'ACTIONS_RECEIPT',
  /** action - successTemplateId */
  ActionsSuccess = 'ACTIONS_SUCCESS',
  /** action - notFoundTemplateId */
  ActionsNotFound = 'ACTIONS_NOT_FOUND',
  /** action - secondaryTemplateId */
  ActionsSecondary = 'ACTIONS_SECONDARY',
  /** catch all for any other workflow config communications */
  Other = 'OTHER',
}

/**
 * phases that a DSR workflow can be in and their execution order
 */
export const DSRWorkflowPhases = [
  WorkflowPhase.Initialization,
  WorkflowPhase.Verification,
  WorkflowPhase.Enrichment,
  WorkflowPhase.DataPointAction,
  WorkflowPhase.JobsCompleted,
] as const;

/**
 * execution order of phases for a given trigger
 */
export const WorkflowPhaseMapping = {
  [WorkflowTrigger.Request]: DSRWorkflowPhases,
} as const;

/**
 * the io-ts union type really annoying
 */
export const sourceModelIds = (
  Object.values(WorkflowJobSourceModelName) as [
    WorkflowJobSourceModelName,
    WorkflowJobSourceModelName,
    ...WorkflowJobSourceModelName[],
  ]
).map((mn) => dbModelId(mn));

export const CWorkflowJobSourceModelId = t.union([
  sourceModelIds[0],
  sourceModelIds[1],
  ...sourceModelIds.slice(2),
]);

/**
 * type for codejk
 */
export type WorkflowJobSourceModelId = t.TypeOf<
  typeof CWorkflowJobSourceModelId
>;

export const destinationModelIds = (
  Object.values(WorkflowJobDestinationModelName) as [
    WorkflowJobDestinationModelName,
    WorkflowJobDestinationModelName,
    ...WorkflowJobDestinationModelName[],
  ]
).map((mn) => dbModelId(mn));

export const CWorkflowJobDestinationId = t.union([
  destinationModelIds[0],
  destinationModelIds[1],
  ...destinationModelIds.slice(2),
]);

/**
 * Type helper for getting phases of a specific trigger
 */
export type RequestTriggerPhases =
  (typeof WorkflowPhaseMapping)[WorkflowTrigger.Request][number];
